/**
 * OrganizationOpportunities Component
 * Displays opportunities posted by an organization on their detail page.
 * Shows published opportunities to all visitors, with management controls for owners/admins.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    ListGroup,
    Modal,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useOpportunityApi } from '@/shared/services/opportunityApi';

import { getTypeColor } from '../utils/opportunityColors';

import '../Opportunities.css';

const STATUS_VARIANTS = {
    draft: 'secondary',
    published: 'success',
    archived: 'warning',
    expired: 'danger',
};

OrganizationOpportunities.propTypes = {
    organizationId: PropTypes.string.isRequired,
    canEdit: PropTypes.bool,
};

export default function OrganizationOpportunities({ organizationId, canEdit }) {
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);

    const opportunityApi = useOpportunityApi();

    const loadOpportunities = useCallback(async () => {
        try {
            setLoading(true);
            const params = canEdit ? {} : { status: 'published' };
            const data = await opportunityApi.fetchByOrganization(
                organizationId,
                params
            );
            setOpportunities(data.opportunities || []);
        } catch (err) {
            console.error('Failed to load opportunities:', err);
            setError('Failed to load opportunities');
        } finally {
            setLoading(false);
        }
    }, [organizationId, canEdit, opportunityApi]);

    useEffect(() => {
        loadOpportunities();
    }, [loadOpportunities]);

    const handlePublish = async (id) => {
        try {
            setActionLoading(id);
            await opportunityApi.publishOpportunity(id);
            setSuccessMsg('Opportunity published');
            setTimeout(() => setSuccessMsg(''), 3000);
            await loadOpportunities();
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to publish opportunity'
            );
        } finally {
            setActionLoading(null);
        }
    };

    const handleArchive = async (id) => {
        try {
            setActionLoading(id);
            await opportunityApi.archiveOpportunity(id);
            setSuccessMsg('Opportunity archived');
            setTimeout(() => setSuccessMsg(''), 3000);
            await loadOpportunities();
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to archive opportunity'
            );
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setActionLoading(deleteTarget);
            await opportunityApi.deleteOpportunity(deleteTarget);
            setDeleteTarget(null);
            setSuccessMsg('Opportunity deleted');
            setTimeout(() => setSuccessMsg(''), 3000);
            await loadOpportunities();
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to delete opportunity'
            );
        } finally {
            setActionLoading(null);
        }
    };

    // Don't show section if not an editor and no published opportunities
    if (!canEdit && !loading && opportunities.length === 0) {
        return null;
    }

    return (
        <>
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="text-primary mb-0">Opportunities</h5>
                    {canEdit && (
                        <Button
                            as={Link}
                            to={`/organizations/${organizationId}/opportunities/new`}
                            variant="primary"
                            size="sm"
                            className="rounded-pill"
                        >
                            + Add Opportunity
                        </Button>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {error && (
                        <Alert
                            variant="danger"
                            className="m-3 mb-0"
                            dismissible
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}
                    {successMsg && (
                        <Alert variant="success" className="m-3 mb-0">
                            {successMsg}
                        </Alert>
                    )}

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner
                                animation="border"
                                variant="primary"
                                size="sm"
                            />
                        </div>
                    ) : opportunities.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            {canEdit ? (
                                <p className="mb-0">
                                    No opportunities yet. Click &quot;Add
                                    Opportunity&quot; to create one.
                                </p>
                            ) : (
                                <p className="mb-0">
                                    No opportunities posted yet.
                                </p>
                            )}
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {opportunities.map((opp) => (
                                <ListGroup.Item
                                    key={opp._id}
                                    className="d-flex justify-content-between align-items-start py-3"
                                >
                                    <div className="me-3">
                                        <Link
                                            to={`/opportunities/${opp._id}`}
                                            className="text-decoration-none"
                                        >
                                            <strong className="text-primary">
                                                {opp.title}
                                            </strong>
                                        </Link>
                                        <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                            {/* CSS custom property for dynamic type color -- must be inline */}
                                            <Badge
                                                bg=""
                                                className="type-color-badge"
                                                style={{
                                                    '--type-color':
                                                        getTypeColor(opp.type),
                                                }}
                                            >
                                                {opp.type}
                                            </Badge>
                                            {canEdit && (
                                                <Badge
                                                    bg={
                                                        STATUS_VARIANTS[
                                                            opp.status
                                                        ] || 'secondary'
                                                    }
                                                >
                                                    {opp.status}
                                                </Badge>
                                            )}
                                            {opp.location?.isRemote && (
                                                <span className="text-muted small">
                                                    Remote
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {canEdit && (
                                        <div className="d-flex gap-1 flex-shrink-0">
                                            <Button
                                                as={Link}
                                                to={`/organizations/${organizationId}/opportunities/${opp._id}/edit`}
                                                variant="outline-primary"
                                                size="sm"
                                            >
                                                Edit
                                            </Button>
                                            {opp.status === 'draft' && (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    onClick={() =>
                                                        handlePublish(opp._id)
                                                    }
                                                    disabled={
                                                        actionLoading ===
                                                        opp._id
                                                    }
                                                >
                                                    {actionLoading ===
                                                    opp._id ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        'Publish'
                                                    )}
                                                </Button>
                                            )}
                                            {opp.status === 'published' && (
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleArchive(opp._id)
                                                    }
                                                    disabled={
                                                        actionLoading ===
                                                        opp._id
                                                    }
                                                >
                                                    {actionLoading ===
                                                    opp._id ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        'Archive'
                                                    )}
                                                </Button>
                                            )}
                                            {(opp.status === 'draft' ||
                                                opp.status === 'archived') && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() =>
                                                        setDeleteTarget(opp._id)
                                                    }
                                                    disabled={
                                                        actionLoading ===
                                                        opp._id
                                                    }
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteTarget}
                onHide={() => setDeleteTarget(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Opportunity</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this opportunity? This
                    action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setDeleteTarget(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={!!actionLoading}
                    >
                        {actionLoading ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
